import { meldeBrowserPanelBereit } from './boundaries/browserPanelBesitz';

/**
 * Welche Browser-Panels haengen in DIESEM Fenster gerade im DOM?
 *
 * Der Server merkt sich je Panel, welches Fenster es per `/browser-panel/ready`
 * angemeldet hat, und schickt eval/click/type/keypress gezielt dorthin. Abgemeldet
 * wird dieser Anspruch nur, wenn das Fenster die Verbindung verliert — nicht, wenn
 * das Panel verschwindet (Rafael wechselt die Buehne, ein Reiter wird ausgehaengt).
 * Dann landete jeder Befehl in einem Fenster ohne passenden Empfaenger und lief
 * stumm in den Timeout, waehrend die Fehlermeldung „owner is still alive" sagte
 * (gemessen 06.10.2026, b-gewinnspiele-fernsteuerung-reparatur-20261006: 19 Kacheln,
 * jede eval 10 s Timeout, waehrend dasselbe Fenster die sichtbaren Kacheln einer
 * anderen Buehne in Millisekunden beantwortete).
 *
 * Mit diesem Register kann App.tsx einen Befehl fuer ein nicht gemountetes Panel
 * sofort und ausdruecklich ablehnen; der Server entzieht daraufhin den Anspruch.
 * Ein Zaehler statt einer Menge, weil dieselbe Kennung kurz doppelt gemountet sein
 * kann (Remount mit neuem Schluessel: neues Panel meldet sich an, bevor das alte geht).
 */
const gemountet = new Map<string, number>();

function schluessel(panelId: string): string {
  return panelId.replace(/^#/, '');
}

/**
 * Meldet ein Panel an; die zurueckgegebene Funktion meldet es wieder ab und sagt,
 * ob damit die LETZTE Instanz dieser Kennung in diesem Fenster weg ist — erst dann
 * gibt BrowserPanel den Besitz am Server zurueck (/browser-panel/gone).
 */
export function meldeBrowserPanelAn(panelId: string): () => boolean {
  const k = schluessel(panelId);
  gemountet.set(k, (gemountet.get(k) ?? 0) + 1);
  return () => {
    const n = (gemountet.get(k) ?? 0) - 1;
    if (n > 0) { gemountet.set(k, n); return false; }
    gemountet.delete(k);
    return true;
  };
}

export function istBrowserPanelGemountet(panelId: string): boolean {
  return gemountet.has(schluessel(panelId));
}

/**
 * Meldet alle in diesem Fenster wirklich gemounteten Browserpanels fuer eine
 * neue WebSocket-Verbindung erneut beim Server an.
 *
 * Der Server haelt die Besitzer nur im Speicher. Nach seinem Neustart bekommt
 * das weiterlaufende Desktop-Fenster per `client:hello` eine neue clientId,
 * waehrend die bereits sichtbaren BrowserPanel-Komponenten nicht neu mounten.
 * Ihr einmaliger Mount-Effekt kann den neuen Server deshalb nicht kennen. Das
 * lokale Register ist dagegen weiterhin die direkte Wahrheit darueber, welche
 * Panels dieses Fenster gerade tatsaechlich enthaelt.
 */
export async function meldeGemounteteBrowserPanelsBereit(clientId: string): Promise<void> {
  const panelIds = [...gemountet.keys()];
  await Promise.all(panelIds.map(panelId => meldeBrowserPanelBereit(panelId, clientId)));
}
